import * as React from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescriptionHidden,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateView } from '@/hooks/use-views'
import { useHasCapability } from '@/hooks/use-permissions'
import { showSuccess } from '@/lib/toast'
import {
  createViewSchema,
  type CreateViewFormValues,
} from '@/lib/schemas/view.schema'
import type {
  ViewEntityType,
  CreateViewColumn,
  CreateViewFilter,
  CreateViewSort,
} from '@/types/api'

interface CreateViewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entityType: ViewEntityType
  /** Modal mode: 'new' creates a blank view, 'saveAs' preserves current filters/sorts */
  mode?: 'new' | 'saveAs'
  /** Current columns to capture in the new view */
  currentColumns: CreateViewColumn[]
  /** Current filters to capture in the new view */
  currentFilters?: CreateViewFilter[]
  /** Current sorts to capture in the new view */
  currentSorts?: CreateViewSort[]
  /** Called after successful creation with the new view ID */
  onSuccess?: (viewId: string) => void
}

export function CreateViewModal({
  open,
  onOpenChange,
  entityType,
  mode = 'new',
  currentColumns,
  currentFilters = [],
  currentSorts = [],
  onSuccess,
}: CreateViewModalProps) {
  const canCreateShared = useHasCapability('view:create:shared')
  const form = useForm<CreateViewFormValues>({
    resolver: standardSchemaResolver(createViewSchema),
    defaultValues: {
      name: '',
      visibility: 'PERSONAL',
    },
  })

  const createView = useCreateView(entityType)

  // Reset form when modal opens
  React.useEffect(() => {
    if (open) {
      form.reset({
        name: '',
        visibility: 'PERSONAL',
      })
    }
  }, [open, form])

  const onSubmit = async (values: CreateViewFormValues) => {
    try {
      const view = await createView.mutateAsync({
        name: values.name.trim(),
        entityType,
        visibility: values.visibility,
        type: 'PERSONAL',
        isDefault: false,
        columns: currentColumns,
        filters: currentFilters.length > 0 ? currentFilters : undefined,
        sorts: currentSorts.length > 0 ? currentSorts : undefined,
      })

      showSuccess('View created successfully')
      onOpenChange(false)
      onSuccess?.(view.id)
    } catch (error) {
      console.error('Failed to create view:', error)
      // Extract error message from Axios response and show it inline on the name field
      const axiosError = error as { response?: { data?: { message?: string } } }
      const message =
        axiosError?.response?.data?.message || 'Failed to create view'
      form.setError('name', { message })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-150 px-4 py-5')}>
        <DialogHeader>
          <DialogTitle className="text-balance">
            {mode === 'saveAs' ? 'Save as New View' : 'Create New View'}
          </DialogTitle>
          <DialogDescriptionHidden>
            Create a new view with the current table configuration
          </DialogDescriptionHidden>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-3">
              {/* View Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      View Name<span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter view name"
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Visibility Selector - only internal users can create org-wide views */}
              {canCreateShared && (
                <FormField
                  control={form.control}
                  name="visibility"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Visibility<span className="text-destructive">*</span>
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select visibility" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="PERSONAL">Private</SelectItem>
                          <SelectItem value="SHARED">
                            Shared (org-wide)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Buttons */}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createView.isPending}>
                {createView.isPending
                  ? 'Creating...'
                  : mode === 'saveAs'
                    ? 'Save View'
                    : 'Create View'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
